import React, { forwardRef, useEffect, useImperativeHandle, useLayoutEffect, useRef } from 'react';

import { useVirtualizer, VirtualItem, VirtualizerOptions } from '@tanstack/react-virtual';
import { debounce } from 'lodash';

import { classNames } from '@/utils';

import LoadMoreInfinite from '@/components/base/LoadMoreInfinite/LoadMoreInfinite';

const DEFAULT_ITEM_HEIGHT = 72;

type InfiniteScrollProps<T> = Partial<VirtualizerOptions<HTMLDivElement, HTMLDivElement>> & {
    className?: string;
    data: T[];
    hasMore: boolean;
    inverse?: boolean;
    isLoadingMore?: boolean;
    loadMore?: () => void;
    renderItem: (item: T, index: number) => React.ReactNode;
    /**
     * Vertical alignment of the list inside the scrollport.
     * `bottom`: when the list is shorter than the viewport, it sits flush to the bottom (chat-style).
     */
    contentAlignment?: 'top' | 'bottom';
};

export type InfiniteScrollRef = {
    scrollToBottom: () => void;
    scrollToTop: () => void;
    scrollToIndex: (index: number) => void;
    getScrollElement: () => HTMLDivElement | null;
};

const InfiniteScrollComponent = <T,>(
    {
        className,
        data,
        hasMore,
        inverse = false,
        isLoadingMore = false,
        loadMore,
        renderItem,
        contentAlignment = 'top',
        ...rest
    }: InfiniteScrollProps<T>,
    ref: React.Ref<InfiniteScrollRef>
) => {
    const scrollContainer = useRef<HTMLDivElement | null>(null);
    const isLoadingMoreRef = useRef(isLoadingMore);
    const isInitialized = useRef(false);
    const prependAnchorRef = useRef<{ scrollTop: number; totalSize: number } | null>(null);
    const previousDataLengthRef = useRef(data.length);

    const rowVirtualizer = useVirtualizer({
        count: hasMore ? data.length + 1 : data.length,
        getScrollElement: () => scrollContainer.current,
        estimateSize: () => DEFAULT_ITEM_HEIGHT,
        measureElement: (element) =>
            element?.getBoundingClientRect?.()?.height ?? DEFAULT_ITEM_HEIGHT,
        ...rest
    });

    useEffect(() => {
        if (inverse && isLoadingMore && !isLoadingMoreRef.current && scrollContainer.current) {
            prependAnchorRef.current = {
                scrollTop: scrollContainer.current.scrollTop,
                totalSize: rowVirtualizer.getTotalSize()
            };
        }

        isLoadingMoreRef.current = isLoadingMore;
    }, [inverse, isLoadingMore, rowVirtualizer]);

    useImperativeHandle(
        ref,
        () => ({
            scrollToBottom: () => {
                if (data.length > 0 && scrollContainer.current) {
                    // First scroll to last index
                    rowVirtualizer.scrollToIndex(data.length - 1, {
                        align: 'end',
                        behavior: 'auto'
                    });

                    // Then ensure we're truly at the bottom after measurement
                    setTimeout(() => {
                        if (scrollContainer.current) {
                            scrollContainer.current.scrollTop =
                                scrollContainer.current.scrollHeight;
                        }
                    }, 50);
                }
            },
            scrollToTop: () => {
                if (data.length > 0) {
                    rowVirtualizer.scrollToIndex(0, { align: 'start', behavior: 'auto' });
                }
            },
            scrollToIndex: (index: number) => {
                rowVirtualizer.scrollToIndex(index, { align: 'center', behavior: 'auto' });
            },
            getScrollElement: () => scrollContainer.current
        }),
        [rowVirtualizer, data.length]
    );

    useEffect(() => {
        if (inverse && rowVirtualizer && data.length > 0 && !isInitialized.current) {
            rowVirtualizer.scrollToIndex(data.length - 1, { align: 'end', behavior: 'auto' });
            isInitialized.current = true;
        }
    }, [inverse, rowVirtualizer, data.length]);

    useLayoutEffect(() => {
        const container = scrollContainer.current;
        const anchor = prependAnchorRef.current;
        const previousDataLength = previousDataLengthRef.current;

        if (!inverse || !container || !anchor) {
            previousDataLengthRef.current = data.length;
            return;
        }

        const hasPrependedItems = data.length > previousDataLength;

        if (hasPrependedItems) {
            const newTotalSize = rowVirtualizer.getTotalSize();
            const sizeDelta = newTotalSize - anchor.totalSize;

            if (sizeDelta !== 0) {
                container.scrollTop = anchor.scrollTop + sizeDelta;
            }

            prependAnchorRef.current = null;
        }

        previousDataLengthRef.current = data.length;
    }, [inverse, data.length, rowVirtualizer]);

    const getEndItem = () => {
        const items = rowVirtualizer.getVirtualItems();
        return inverse ? items[0] : items[items.length - 1];
    };

    const isNearTheEnd = () => {
        const endItem = getEndItem();

        if (!endItem) return false;

        return inverse ? endItem.index <= 1 : endItem.index >= data.length - 2;
    };

    const handleLoadMore = debounce(() => {
        if (isNearTheEnd() && !isLoadingMoreRef.current && rowVirtualizer.isScrolling) {
            loadMore?.();
        }
    }, 100);

    // Handle load more when scrolling near the end
    useEffect(() => {
        if (hasMore) {
            handleLoadMore();
        }
    }, [hasMore, getEndItem()]);

    const handleItemRender = (index: number) => {
        const item = data[index];
        return item ? renderItem(item, index) : null;
    };

    return (
        <div
            ref={scrollContainer}
            className={classNames(
                'h-full w-full overflow-auto',
                contentAlignment === 'bottom' && 'flex min-h-0 flex-col',
                className
            )}>
            <div
                className={classNames(
                    'relative w-full shrink-0',
                    contentAlignment === 'bottom' && 'mt-auto'
                )}
                style={{ height: `${rowVirtualizer.getTotalSize()}px` }}>
                {rowVirtualizer.getVirtualItems().map((virtualRow: VirtualItem) => {
                    const showLoader = inverse
                        ? virtualRow.index === 0 && hasMore
                        : virtualRow.index === data.length && hasMore;

                    return (
                        <div
                            key={virtualRow.key}
                            data-index={virtualRow.index}
                            ref={rowVirtualizer.measureElement}
                            className="absolute top-0 w-full"
                            style={{ transform: `translateY(${virtualRow.start}px)` }}>
                            {showLoader ? (
                                <LoadMoreInfinite className="flex justify-center py-3" />
                            ) : (
                                handleItemRender(virtualRow.index)
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export const InfiniteScroll = forwardRef(InfiniteScrollComponent) as <T>(
    props: InfiniteScrollProps<T> & { ref?: React.Ref<InfiniteScrollRef> }
) => React.ReactElement;
